/**
 * Mytherra Blockchain API Service
 * Direkte RPC-Verbindung zur Mytherra Node
 */

// API Base URL - automatisch localhost für Dev, /api/ für Production
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:3002' : '/api';

const PROXY_URL = `${API_BASE}/rpc`;
const PROXY_EXPLORER_URL = `${API_BASE}/explorer`;
const PROXY_DASHBOARD_URL = `${API_BASE}/dashboard`;

// RPC Call Helper Funktion (auch exportiert für direkten Zugriff)
export const rpcCall = async (method, params = []) => {
  try {
    const response = await fetch(PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        jsonrpc: '1.0',
        id: Date.now(),
        method: method,
        params: params
      })
    });

    if (!response.ok) {
      throw new Error(`RPC Error: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.error) {
      throw new Error(`RPC Error: ${data.error.message}`);
    }

    return data.result;
  } catch (error) {
    console.error(`RPC Call Error (${method}):`, error);
    return null;
  }
};

export const mytherraApi = {
  // Export rpcCall for direct RPC access
  rpcCall,

  /**
   * Get current block count (block height)
   * @returns {Promise<number>}
   */
  getBlockCount: async () => {
    return await rpcCall('getblockcount');
  },

  /**
   * Get number of network connections
   * @returns {Promise<number>}
   */
  getConnectionCount: async () => {
    return await rpcCall('getconnectioncount');
  },

  /**
   * Get current difficulty
   * @returns {Promise<number>}
   */
  getDifficulty: async () => {
    return await rpcCall('getdifficulty');
  },

  /**
   * Get network hashrate
   * @returns {Promise<number>}
   */
  getNetworkHashps: async () => {
    return await rpcCall('getnetworkhashps');
  },

  /**
   * Get blockchain info
   * @returns {Promise<Object>}
   */
  getBlockchainInfo: async () => {
    return await rpcCall('getblockchaininfo');
  },

  /**
   * Get network info
   * @returns {Promise<Object>}
   */
  getNetworkInfo: async () => {
    return await rpcCall('getnetworkinfo');
  },

  /**
   * Get wallet info
   * @returns {Promise<Object>}
   */
  getWalletInfo: async () => {
    return await rpcCall('getwalletinfo');
  },

  /**
   * Get balance
   * @returns {Promise<number>}
   */
  getBalance: async () => {
    return await rpcCall('getbalance');
  },

  /**
   * Get new address
   * @returns {Promise<string>}
   */
  getNewAddress: async () => {
    return await rpcCall('getnewaddress');
  },

  /**
   * Get block hash by height
   * @param {number} height - Block height
   * @returns {Promise<string>}
   */
  getBlockHash: async (height) => {
    return await rpcCall('getblockhash', [height]);
  },

  /**
   * Get block by hash
   * @param {string} hash - Block hash
   * @returns {Promise<Object>}
   */
  getBlock: async (hash) => {
    return await rpcCall('getblock', [hash]);
  },

  /**
   * Get transaction
   * @param {string} txid - Transaction ID
   * @param {boolean} verbose - Verbose output
   * @returns {Promise<Object>}
   */
  getTransaction: async (txid, verbose = true) => {
    return await rpcCall('gettransaction', [txid, verbose]);
  },

  /**
   * List recent transactions
   * @param {number} count - Number of transactions
   * @returns {Promise<Array>}
   */
  listTransactions: async (count = 10) => {
    return await rpcCall('listtransactions', ['*', count]);
  },

  /**
   * Get mempool information
   * @returns {Promise<Object>}
   */
  getMempoolInfo: async () => {
    return await rpcCall('getmempoolinfo');
  },

  /**
   * Send MYT to address (using client-side wallet)
   * @param {string} address - Recipient address
   * @param {number} amount - Amount in MYT
   * @returns {Promise<string>} Transaction ID
   */
  sendToAddress: async (toAddress, amount) => {
    try {
      // Import walletService and storageService
      const { default: walletService } = await import('./walletService');
      const { default: storageService } = await import('./storageService');
      
      // Get private key from session
      const privateKeyWif = storageService.loadSession();
      
      if (!privateKeyWif) {
        throw new Error('No wallet loaded. Please sign in first.');
      }
      
      // Get from address
      const fromAddress = await walletService.getAddressFromWif(privateKeyWif);
      
      if (!fromAddress) {
        throw new Error('Could not derive address from private key');
      }
      
      // Send transaction using wallet service with correct parameters
      const result = await walletService.sendTransaction(
        PROXY_URL,  // rpcUrl
        '',         // rpcUser (not needed, proxy handles auth)
        '',         // rpcPassword (not needed, proxy handles auth)
        privateKeyWif,
        fromAddress,
        toAddress,
        amount
      );
      
      if (!result || !result.success) {
        throw new Error(result?.message || 'Transaction broadcast failed');
      }
      
      return result.txid;
    } catch (error) {
      console.error('Error sending transaction:', error);
      throw error;
    }
  },

  /**
   * Get money supply from Explorer API
   * @returns {Promise<number>}
   */
  getMoneySupply: async () => {
    try {
      const response = await fetch(`${API_BASE}/moneysupply`, {
        method: 'GET',
        headers: {
          'Accept': 'text/plain'
        }
      });

      if (!response.ok) {
        throw new Error(`Money supply API Error: ${response.status}`);
      }

      const supply = await response.text();
      return parseFloat(supply);
    } catch (error) {
      console.error('Error fetching money supply:', error);
      // Fallback: Calculate from block subsidy
      const blockCount = await mytherraApi.getBlockCount();
      if (blockCount) {
        // Geschätzte Supply basierend auf Blockanzahl
        // Anpassen basierend auf Mytherra's Block Reward
        return blockCount * 50; // Beispiel: 50 MYT pro Block
      }
      return null;
    }
  },

  /**
   * Get complete network status
   * @returns {Promise<Object>}
   */
  getNetworkStatus: async () => {
    try {
      const [blockCount, connectionCount, difficulty, hashrate] = await Promise.all([
        mytherraApi.getBlockCount(),
        mytherraApi.getConnectionCount(),
        mytherraApi.getDifficulty(),
        mytherraApi.getNetworkHashps()
      ]);

      return {
        blockHeight: blockCount,
        networkPeers: connectionCount,
        difficulty: difficulty,
        hashrate: hashrate,
        status: connectionCount > 0 ? 'Online' : 'Offline'
      };
    } catch (error) {
      console.error('Error fetching network status:', error);
      return {
        blockHeight: 0,
        networkPeers: 0,
        difficulty: 0,
        hashrate: 0,
        status: 'Offline'
      };
    }
  },

  /**
   * Get wallet data
   * @returns {Promise<Object>}
   */
  getWalletData: async () => {
    try {
      const [balance, walletInfo, transactions] = await Promise.all([
        mytherraApi.getBalance(),
        mytherraApi.getWalletInfo(),
        mytherraApi.listTransactions(100)
      ]);

      // Berechne Sent und Received aus Transaktionen
      let sent = 0;
      let received = 0;

      if (transactions && Array.isArray(transactions)) {
        transactions.forEach(tx => {
          if (tx.category === 'send') {
            sent += Math.abs(tx.amount);
          } else if (tx.category === 'receive') {
            received += tx.amount;
          }
        });
      }

      return {
        balance: balance ? balance.toFixed(2) : '0.00',
        sent: sent.toFixed(2),
        received: received.toFixed(2),
        txCount: transactions ? transactions.length : 0
      };
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      return {
        balance: '0.00',
        sent: '0.00',
        received: '0.00',
        txCount: 0
      };
    }
  },

  /**
   * Get last transactions
   * @param {number} count - Number of transactions
   * @returns {Promise<Array>}
   */
  getLastTxs: async (count = 10) => {
    try {
      const transactions = await mytherraApi.listTransactions(count);
      
      if (!transactions || !Array.isArray(transactions)) {
        return [];
      }

      // Formatiere für Dashboard
      return transactions.map(tx => ({
        txid: tx.txid,
        time: tx.time,
        amount: tx.amount,
        category: tx.category,
        confirmations: tx.confirmations,
        address: tx.address
      }));
    } catch (error) {
      console.error('Error fetching last transactions:', error);
      return [];
    }
  },

  /**
   * Format MYT amount
   * @param {number} amount - Amount in MYT
   * @returns {string}
   */
  formatMYT: (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toFixed(8).replace(/\.?0+$/, '');
  },

  /**
   * Test RPC connection
   * @returns {Promise<boolean>}
   */
  testConnection: async () => {
    try {
      const blockCount = await mytherraApi.getBlockCount();
      return blockCount !== null;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  },

  // ============================================
  // EXPLORER API FUNCTIONS
  // ============================================

  /**
   * Get explorer stats
   * @returns {Promise<Object>}
   */
  getExplorerStats: async () => {
    try {
      const response = await fetch(`${PROXY_EXPLORER_URL}/api/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Explorer API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching explorer stats:', error);
      return null;
    }
  },

  /**
   * Get dashboard data (combined RPC + Explorer)
   * @returns {Promise<Object>}
   */
  getDashboardData: async () => {
    try {
      const response = await fetch(PROXY_DASHBOARD_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Dashboard API Error: ${response.status}`);
      }

      const data = await response.json();
      
      // Transform to a more user-friendly format
      return {
        blockHeight: data.blockCount || 0,
        difficulty: data.difficulty || 0,
        networkPeers: data.connections || 0,
        hashrate: data.hashrate || 0,
        moneySupply: data.moneySupply || 0,
        status: data.status || 'offline',
        blockchain: data.blockchain || null,
        network: data.network || null,
        timestamp: data.timestamp
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return {
        blockHeight: 0,
        difficulty: 0,
        networkPeers: 0,
        hashrate: 0,
        moneySupply: 0,
        status: 'offline',
        blockchain: null,
        network: null,
        timestamp: new Date().toISOString()
      };
    }
  },

  /**
   * Get block information from explorer
   * @param {string|number} blockHashOrHeight - Block hash or height
   * @returns {Promise<Object>}
   */
  getExplorerBlock: async (blockHashOrHeight) => {
    try {
      const response = await fetch(`${PROXY_EXPLORER_URL}/api/block/${blockHashOrHeight}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Explorer API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching block from explorer:', error);
      return null;
    }
  },

  /**
   * Get transaction from explorer
   * @param {string} txid - Transaction ID
   * @returns {Promise<Object>}
   */
  getExplorerTransaction: async (txid) => {
    try {
      const response = await fetch(`${PROXY_EXPLORER_URL}/api/tx/${txid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Explorer API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching transaction from explorer:', error);
      return null;
    }
  },

  /**
   * Get address information from explorer
   * @param {string} address - Wallet address
   * @returns {Promise<Object>}
   */
  getExplorerAddress: async (address) => {
    console.log(`Fetching address data for: ${address}`);
    
    // Try Explorer API first (fast)
    try {
      console.log('Trying Explorer /ext/getaddress');
      const response = await fetch(`${PROXY_EXPLORER_URL}/ext/getaddress?address=${address}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Success with Explorer API:`, data);
        return data;
      }
    } catch (error) {
      console.log(`Explorer API failed:`, error.message);
    }

    // Fallback to RPC scantxoutset
    try {
      console.log('Fallback to RPC scantxoutset');
      const result = await rpcCall('scantxoutset', ['start', [`addr(${address})`]]);
      
      if (result && result.total_amount !== undefined) {
        console.log(`✅ Success with RPC scantxoutset:`, result);
        return {
          balance: result.total_amount,
          received: result.total_amount,
          txCount: result.unspents ? result.unspents.length : 0,
          unspents: result.unspents || []
        };
      }
    } catch (error) {
      console.log(`RPC scantxoutset failed:`, error.message);
    }

    console.error('❌ Failed to fetch address data for:', address);
    return null;
  },

  /**
   * Get recent blocks from explorer
   * @param {number} limit - Number of blocks to fetch
   * @returns {Promise<Array>}
   */
  getExplorerRecentBlocks: async (limit = 10) => {
    try {
      const response = await fetch(`${PROXY_EXPLORER_URL}/api/blocks?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Explorer API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching recent blocks:', error);
      return [];
    }
  },

  /**
   * Search blockchain data in explorer
   * @param {string} query - Search query (block, tx, address)
   * @returns {Promise<Object>}
   */
  explorerSearch: async (query) => {
    try {
      const response = await fetch(`${PROXY_EXPLORER_URL}/api/search/${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Explorer API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching explorer:', error);
      return null;
    }
  }
};

export default mytherraApi;
